import { buildQueryString, request, serializeCsvQuery } from "../../client"
import type { AvailabilityPolicyConfig } from "@kontextwerk/control-web-frame/types/availabilityPolicies"
import {
  archiveRegistryRoots,
  restoreRegistryRoots,
  type RegistryArchiveBatchResponse,
} from "../../archive"

export type CustomerListQuery = {
  organizationId?: string | null
  customerIds?: string[] | null
  query?: string | null
  addressQuery?: string | null
  customerType?: CustomerType | null
  missingContactData?: boolean | null
  contactData?: string | null
  role?: string | null
  propertyId?: string | null
  sortBy?: string | null
  sortDir?: "asc" | "desc" | null
  offset?: number | null
  limit?: number | null
  archiveScope?: ArchiveScope | null
}

export type CustomerListEntry = Customer & {
  primary_contact_value?: string | null
  primary_contact_kind?: string | null
  primary_contact_label?: string | null
  primary_address_label?: string | null
  primary_property_id?: string | null
}

export type CustomerListResponse = {
  items: CustomerListEntry[]
  total: number
}

export type CustomerTableProjection = CustomerListResponse & {
  properties: Property[]
  property_customers: PropertyCustomer[]
  contact_points: ContactPoint[]
}

export type CustomerDetailProjection = {
  customer: Customer
  properties: Property[]
  property_customers: PropertyCustomer[]
  contact_points: ContactPoint[]
}

export type CustomerAppointmentAvailabilityResponse = {
  availability: AvailabilityPolicyConfig | null
}

export type CustomerAppointmentAvailabilityUpdatePayload = {
  availability: AvailabilityPolicyConfig | null
}

const customerListQueryParams = (query?: CustomerListQuery) => ({
  organization_id: query?.organizationId,
  customer_ids: serializeCsvQuery(query?.customerIds),
  query: query?.query,
  address_query: query?.addressQuery,
  customer_type: query?.customerType,
  missing_contact_data: query?.missingContactData,
  contact_data: query?.contactData,
  role: query?.role,
  property_id: query?.propertyId,
  sort_by: query?.sortBy,
  sort_dir: query?.sortDir,
  offset: query?.offset,
  limit: query?.limit,
  archive_scope: query?.archiveScope,
})

export const fetchCustomers = (query?: CustomerListQuery) =>
  request<CustomerListResponse>(
    `/customer-registry/customers${buildQueryString(customerListQueryParams(query))}`,
  )

export const fetchCustomerTable = (query?: CustomerListQuery) =>
  request<CustomerTableProjection>(
    `/customer-registry/customers${buildQueryString({
      projection: "table",
      ...customerListQueryParams(query),
    })}`,
  )

export const createCustomer = (payload: CustomerCreatePayload) =>
  request<Customer>("/customer-registry/customers", {
    method: "POST",
    body: JSON.stringify(payload),
  })

export const updateCustomer = (customerId: string, payload: CustomerUpdatePayload) =>
  request<Customer>(`/customer-registry/customers/${customerId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  })

export const fetchCustomer = (customerId: string) =>
  request<Customer>(`/customer-registry/customers/${customerId}`)

export const fetchCustomerDetailProjection = (customerId: string) =>
  request<CustomerDetailProjection>(
    `/customer-registry/customers/${customerId}${buildQueryString({
      projection: "detail",
    })}`,
  )

export const fetchCustomerAppointmentAvailability = (customerId: string) =>
  request<CustomerAppointmentAvailabilityResponse>(
    `/customer-registry/customers/${customerId}/appointment-availability`,
  )

export const updateCustomerAppointmentAvailability = (
  customerId: string,
  payload: CustomerAppointmentAvailabilityUpdatePayload,
) =>
  request<CustomerAppointmentAvailabilityResponse>(
    `/customer-registry/customers/${customerId}/appointment-availability`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
  )

export const archiveCustomersBatch = (
  customerIds: string[],
  reason: ArchiveReason = "USER_REQUEST",
): Promise<RegistryArchiveBatchResponse> =>
  archiveRegistryRoots(
    "/customer-registry/customers/archive-batch",
    "customer",
    customerIds,
    reason,
  )

export const restoreCustomersBatch = (
  customerIds: string[],
): Promise<RegistryArchiveBatchResponse> =>
  restoreRegistryRoots("/customer-registry/customers/restore-batch", "customer", customerIds)
